import React, { CSSProperties, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { InputAdornment, OutlinedInput, Slider } from '@mui/material';
import { SliderThumb as MuiSliderThumb } from '@mui/material/Slider';
import {
  SEAT_TIMER_SIZE,
  TABLE_TYPE_SITNGO,
  TABLE_TYPE_TOURNAMENT,
  TIME_PROGRESS_BAR_THICKNESS,
  TRAY_CONTAINED_MEDIA_QUERY,
  TRAY_EDGE_GUTTER_PX,
  TRAY_MAX_WIDTH_PX
} from 'constants/gameConstants';
import GameContext from 'contexts/GameContext';
import { useGridMode } from 'contexts/GridModeContext';
import { useGridCellWidth } from 'hooks/useGridCellWidth';
import { useKeyboardOffset } from 'hooks/useKeyboardOffset';
import { useOrientation } from 'hooks/useOrientation';
import { useTableSelector } from 'hooks/useTableSelector';
import { saloonFonts, saloonTokens } from 'styles/saloonTheme';
import {
  calculateStepValue,
  currencyFormatter,
  toChips,
  toLocaleStringNumber
} from 'utils/functions';

import SeatTimer from 'components/Timer/SeatTimer';

// EN-10420: the in-tray countdown is the seat's timer rendered at EXACTLY the
// POV avatar's on-screen size — a pixel-true duplicate (same ring thickness,
// same number typography), not a re-sized lookalike. The avatar ring's size
// is the product of several nested scales (the 0.5 avatar block, the table's
// responsive --seat-scale, the player's Table View Size), so instead of
// re-deriving the chain we MEASURE the seat ring's rendered width
// ([data-seat-timer-ring="seat"], present whenever this tray is — same turn
// gate) and render at measured/full ratio. Fallback if unmeasurable:
const TRAY_TIMER_FALLBACK_SCALE = 0.5;
const trayTimerCellStyle = (scale: number): CSSProperties => ({
  position: 'relative',
  width: Math.round(SEAT_TIMER_SIZE * scale),
  height: Math.round(SEAT_TIMER_SIZE * scale),
  overflow: 'visible'
});
const trayTimerScalerStyle = (scale: number): CSSProperties => ({
  position: 'absolute',
  top: TIME_PROGRESS_BAR_THICKNESS * 2.25 * scale,
  left: TIME_PROGRESS_BAR_THICKNESS * 2.25 * scale,
  transform: `scale(${scale})`,
  transformOrigin: 'top left'
});

/**
 * Isolated so the per-second server clock tick (game.time) re-renders ONLY
 * this ring — not the whole tray. A tray re-render collapses the amount
 * field's select-all (react-number-format repositions the caret on every
 * render), which made the selection flash and die within a second of focus.
 */
const TrayCountdownRing = ({ show, scale }: { show: boolean; scale: number }) => {
  const timerStart = useTableSelector((table) => table.game.lastmove);
  const timerCurrent = useTableSelector((table) => table.game.time);
  const timerTarget = useTableSelector((table) => table.game.speed);
  if (!show) return null;
  return (
    <div style={trayTimerCellStyle(scale)}>
      <div style={trayTimerScalerStyle(scale)}>
        <SeatTimer
          presentationOnly
          start={timerStart}
          current={timerCurrent}
          target={timerTarget}
          isStartProgress={show}
          size={SEAT_TIMER_SIZE}
        />
      </div>
    </div>
  );
};

const RaiseActionExpanded = ({ setAmount, amount, raiseButtonRef }: any) => {
  // EN-10420: announce the tray for exactly as long as it is mounted. The POV
  // seat lifts its hole cards clear of the tray while this is true
  // (Seat/modes/PortraitCurrentPlayer). Keyed to mount/unmount rather than the
  // Raise button's toggle so every opener — production click, story
  // force-mount — drives the same card behavior.
  const { setIsBetTrayOpen, isTimerOff } = useContext(GameContext);
  useEffect(() => {
    setIsBetTrayOpen(true);
    return () => setIsBetTrayOpen(false);
  }, [setIsBetTrayOpen]);

  // EN-10420: in-tray countdown gate — mirrors the seat's own gate
  // (SeatWithPlayer) so the two rings can never disagree, PLUS portrait-only:
  // the tray only occludes the seat's ring in portrait. In landscape the seat
  // ring stays fully visible, so a second ring is redundant there (design
  // call, Jason 2026-08-10 — see the Desktop Landscape decision story). The
  // multi-table grid forces portrait per cell, so grid cells still show it.
  // The clock fields themselves live in TrayCountdownRing so their per-second
  // ticks re-render only the ring, never the amount field.
  const orientation = useOrientation();
  const isPortrait = orientation === 'portrait';
  const gameHand = useTableSelector((table) => table.game.hand);
  const gameMove = useTableSelector((table) => table.game.move);
  const startTurn = useTableSelector((table) => table.gameState.startTurn);
  const povSeatId = useTableSelector((table) => table.currentPlayer.seatId);
  const isMultiRunActive = useTableSelector((table) => table.currentPlayer.isMultiRunActive);
  const showTrayCountdown =
    isPortrait &&
    String(gameMove) === String(povSeatId) &&
    !!startTurn &&
    +gameHand < 12 &&
    +gameHand > 4 &&
    !isMultiRunActive &&
    !isTimerOff;

  // Match the avatar ring's rendered size exactly (see the module comment).
  // The lookup is scoped to this tray's own table view: [data-table-view-root]
  // wraps a table AND its footer in both single-table and grid mode (Game.tsx),
  // so in a multi-table grid the tray can never measure another table's ring.
  // Compositions without the wrapper (stories) fall back to document-wide,
  // where only one table exists anyway.
  const trayRootRef = useRef<HTMLDivElement>(null);
  const [trayTimerScale, setTrayTimerScale] = useState(TRAY_TIMER_FALLBACK_SCALE);
  useEffect(() => {
    let timerId = 0;
    let retries = 0;
    // Retry delays, same idiom as the select-all checkpoint guards below: the
    // seat's ring can land a commit AFTER the gate flips (store updates fan
    // out to subscribers across commits), so a single read at flip time
    // misses it and the fallback scale would stick for the whole turn. Plain
    // timeouts, not rAF — background/throttled tabs freeze animation frames
    // entirely but still run (clamped) timers, so the scale corrects before
    // the player looks back at the tab.
    const RETRY_MS = [50, 150, 300, 600, 1200];
    const read = () => {
      const scope = trayRootRef.current?.closest('[data-table-view-root]') ?? document;
      const seatRing = scope.querySelector('[data-seat-timer-ring="seat"]');
      const w = seatRing ? seatRing.getBoundingClientRect().width : NaN;
      if (Number.isFinite(w) && w > 0) {
        setTrayTimerScale(w / SEAT_TIMER_SIZE);
        return;
      }
      setTrayTimerScale(TRAY_TIMER_FALLBACK_SCALE);
      if (retries < RETRY_MS.length) {
        timerId = window.setTimeout(read, RETRY_MS[retries]);
        retries += 1;
      }
    };
    const onResize = () => {
      retries = 0;
      read();
    };
    read();
    window.addEventListener('resize', onResize);
    return () => {
      window.clearTimeout(timerId);
      window.removeEventListener('resize', onResize);
    };
    // Re-read when the countdown gate flips on — the tray can mount before
    // the turn state exists (the force-mounted TrayOpen acceptance story; a
    // pre-turn open in production).
  }, [showTrayCountdown]);

  const isPennyTable = useTableSelector((table) => table.game.isPennyTable);
  const bb = useTableSelector((table) => table.game.bb);
  const tableType = useTableSelector((table) => table.game.tabletype);
  // Cash tables show real currency ($); tournaments / sit-n-gos show chip counts.
  const isCashTable = tableType !== TABLE_TYPE_TOURNAMENT && tableType !== TABLE_TYPE_SITNGO;
  const formatChips = (val: number) =>
    isCashTable ? currencyFormatter(val) : toLocaleStringNumber(val);
  // Multi-table grid forces portrait, but each cell is a fixed-width box with
  // overflow:hidden — so the portrait tray's full-bleed `width:100vw` (sized for
  // a phone screen) overflows the cell and gets clipped into a broken slice.
  // In grid the tray must size to its container (the cell), not the viewport.
  const isGridMode = useGridMode();
  // The cell's true width — the tray spans it edge to edge (see gridCellPanel).
  const gridCellWidth = useGridCellWidth(trayRootRef, isGridMode);
  // Width variant (EN-10420, Jason + Marlon huddle 8/13): keyed on viewport
  // width ALONE — orientation deliberately does not participate (an iPad held
  // portrait still affords gutters, so it gets the contained panel too). See
  // gameConstants for the flip-point derivation. Replaces the old
  // landscape-only 1024px split.
  const [isContained, setIsContained] = useState(
    () =>
      typeof window !== 'undefined' &&
      (window.matchMedia?.(TRAY_CONTAINED_MEDIA_QUERY)?.matches ?? false)
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(TRAY_CONTAINED_MEDIA_QUERY);
    const onChange = () => setIsContained(mql.matches);
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);
  // Keyboard overlays the webview, so lift the amount-entry tray above it.
  const keyboardOffset = useKeyboardOffset();
  const {
    min,
    x2,
    x3,
    x5,
    oneHalf,
    oneFourth,
    fullPot,
    playersStack,
    x2isDisabled,
    x3isDisabled,
    x5isDisabled,
    oneHalfIsDisabled,
    oneFourthIsDisabled,
    fullPotDisabled,
    allInDisabled,
    hasPotLimit
  } = useTableSelector((table) => table.gameState.pokerActions.betMultiplier);

  // Slider change fires continuously while the user drags (~60Hz on touch).
  // Focusing the raise button mid-drag previously threw a TypeError every
  // frame because the ref is never actually attached to <ActionButton/>; the
  // error itself was swallowed by React but the per-frame error reporting
  // overhead made the slider feel unresponsive on touch ("requires hard
  // press to drag"). Defer the focus to onChangeCommitted so it runs once
  // when the gesture ends, and guard with optional chaining so a null ref
  // can no longer throw.
  const handleRaiseSlider = (e: Event) => {
    setAmount((e?.target as any).value);
  };

  const handleRaiseSliderCommit = () => {
    raiseButtonRef?.current?.focus();
  };

  const updateAmount = useCallback(
    (e: any) => {
      let keyVal = e.target.value;
      if (!isNaN(+e.target.value) || keyVal === '.') {
        // Clamp an over-max entry down to the ceiling (pot on pot-limit tables,
        // otherwise the all-in stack) so the value matches what the input shows
        // and what the slider/steppers/paste already enforce. Without this, a
        // typed amount above the max stayed in state as-is — the field displayed
        // the clamped max but Bet/Raise stayed disabled against the raw value.
        const max = hasPotLimit ? fullPot : playersStack;
        if (keyVal !== '.' && +keyVal > max) keyVal = max;
        setAmount(keyVal);
      }
    },
    [hasPotLimit, fullPot, playersStack, setAmount]
  );

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      raiseButtonRef?.current?.focus();
    }
  };

  /**
   * Armed while the field still holds the amount the player focused, i.e. they
   * have not typed yet. See handleBeforeAmountInput.
   */
  const replaceOnFirstTypeRef = useRef(false);

  /**
   * Makes the FIRST typed character replace the seeded amount, whether or not
   * the select-on-focus below actually held.
   *
   * The selection is the nice version of this and it is attempted first, but it
   * is not something this component can guarantee: the tray is a controlled
   * input that re-renders on every countdown tick and table broadcast,
   * react-number-format repositions the caret in its own focus handler, and a
   * touch browser places the caret from the tap itself — each of which collapses
   * a selection, at a moment none of them agree on. The timed re-selects below
   * chase that and still lose often enough to be reported twice.
   *
   * This path does not care. If the caret is collapsed when the first character
   * arrives, the character becomes the whole value; if the selection DID
   * survive, the browser already replaces the text and this stands aside. Either
   * way one tap and one keystroke re-size the bet, which is the behaviour that
   * was asked for.
   */
  const amountInputRef = useRef<HTMLInputElement | null>(null);

  // Held in a ref and reassigned every render so the listener below can stay
  // registered once while still closing over the current setAmount.
  const beforeAmountInputRef = useRef<(event: InputEvent) => void>(() => undefined);
  beforeAmountInputRef.current = (event: InputEvent) => {
    if (!replaceOnFirstTypeRef.current) return;
    const input = amountInputRef.current;
    if (!input) return;
    // Whatever happens next, the field is the player's from here on.
    replaceOnFirstTypeRef.current = false;
    // Only a plain insertion replaces. A deletion or an IME composition is the
    // player editing what is already there, which is theirs to keep.
    if (event.inputType !== 'insertText' || !event.data) return;
    // The selection survived — the browser is about to replace the text itself.
    if (input.selectionStart !== input.selectionEnd) return;
    // Anything react-number-format would reject anyway is left to it.
    const typed = event.data;
    if (isNaN(+typed) && typed !== '.') return;
    event.preventDefault();
    setAmount(typed);
  };

  /**
   * A NATIVE `beforeinput` listener, not React's `onBeforeInput`.
   *
   * React's is a legacy synthetic assembled from `textInput` / `keypress`, and
   * it does not carry the `inputType` and `data` this needs to tell an
   * insertion from a deletion. The native event carries both and is what mobile
   * soft keyboards actually emit.
   *
   * Attached through a ref CALLBACK rather than an effect so it follows the
   * element itself: the tray swaps between its full-bleed and contained
   * variants, and an effect keyed on a ref object would not see that remount.
   */
  const dispatchBeforeAmountInput = useRef((event: Event) => {
    beforeAmountInputRef.current(event as InputEvent);
  }).current;

  const attachAmountInput = useCallback(
    (node: HTMLInputElement | null) => {
      amountInputRef.current?.removeEventListener('beforeinput', dispatchBeforeAmountInput);
      amountInputRef.current = node;
      node?.addEventListener('beforeinput', dispatchBeforeAmountInput);
    },
    [dispatchBeforeAmountInput]
  );

  const handleAmountBlur = () => {
    replaceOnFirstTypeRef.current = false;
  };

  // Select the whole amount on focus so typing REPLACES it. Players reported
  // having to clear the seeded number digit-by-digit before entering their own
  // size — the previous client selected it for them, so a re-size cost one
  // gesture instead of several mid-turn. Deferred one frame because
  // react-number-format repositions the caret in its own focus handler, which
  // would collapse a selection made synchronously here.
  const handleAmountFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    // event.target, NOT event.currentTarget: React nulls currentTarget once
    // the dispatch completes, and the MUI/react-number-format wrapper chain
    // delivers this event in a way where currentTarget is already gone on
    // real WebKit — the rAF callback then threw ("null is not an object")
    // and select-on-focus silently never ran on device (found via the
    // EN-10420 on-device probe, 2026-08-09). target is the input itself and
    // survives; guard isConnected in case the tray unmounts within the frame.
    const input = event.target as HTMLInputElement;
    const valueAtFocus = input.value;
    // The guarantee behind the selection: even if every re-select below loses
    // its race, the first character the player types still replaces the amount.
    replaceOnFirstTypeRef.current = true;
    requestAnimationFrame(() => {
      if (input.isConnected) input.select();
    });
    // Collapse guards: the selection dies at unpredictable moments in the
    // first half-second — react-number-format's own caret timer, and (the big
    // one on device) iOS dropping the selection while the keyboard animation
    // moves and scrolls the focused field. The first tap after the tray opens
    // always sat in that window, which is why a single early re-check wasn't
    // enough. Re-assert at checkpoints until the dust settles — but ONLY
    // while the value is untouched: the moment the player types, their input
    // owns the field and every remaining checkpoint is a no-op.
    const reselectIfCollapsed = () => {
      if (
        input.isConnected &&
        document.activeElement === input &&
        input.value === valueAtFocus &&
        input.selectionStart === input.selectionEnd
      ) {
        input.select();
      }
    };
    [80, 200, 350, 600].forEach((ms) => window.setTimeout(reselectIfCollapsed, ms));
  };

  const handlePasteValue = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedValue = Number(event.clipboardData.getData('text'));
    if (pastedValue > playersStack) {
      setAmount(playersStack);
    }
  };

  // Stepper bounds/step mirror the slider: penny tables step by the BB-derived
  // value, everything else by 1; clamp between the slider's min and max.
  const step = isPennyTable ? calculateStepValue(bb) : 1;
  const maxAmount = hasPotLimit ? fullPot : playersStack;
  const minAmount = min > playersStack ? playersStack : min;
  // The +/- steppers move by ONE big blind so the amount walks every valid
  // raise-to (e.g. 1200 → 1800 → 2400 on a 300/600 table), scaling per table
  // ($0.50 on a $0.25/$0.50 game, etc). Stepping by `min` was the bug: `min` is
  // the minimum-raise TOTAL (2×BB preflop), so it skipped every other increment
  // (1200 → 2400). Any min+n·BB amount is a legal raise, so this is always valid.
  // Fall back to the slider's fine step only if the engine hasn't reported the BB.
  // EN-8597: the BB is parsed with toChips because payload shapes differ per
  // game type — multi-table tournaments send it comma-grouped ("1,600"), where
  // Number() is NaN and the steppers fell back to 1 chip (1600 → 1601).
  const bigBlind = toChips(bb);
  const stepIncrement = bigBlind > 0 ? bigBlind : step;

  const adjustAmount = (direction: 1 | -1) => {
    const current = Number(amount) || minAmount;
    const next = Math.min(maxAmount, Math.max(minAmount, current + direction * stepIncrement));
    setAmount(isPennyTable ? Number(next.toFixed(2)) : next);
  };

  // 9 evenly spaced decorative ticks along the rail (design slider), only when
  // the range is non-degenerate so MUI doesn't get duplicate mark values.
  const sliderMarks =
    maxAmount > minAmount
      ? Array.from({ length: 9 }, (_, i) => ({
          value: minAmount + ((maxAmount - minAmount) * i) / 8
        }))
      : false;

  const presets = [
    { label: '1/4', value: oneFourth, disabled: oneFourthIsDisabled },
    { label: '1/2', value: oneHalf, disabled: oneHalfIsDisabled },
    { label: '2x', value: x2, disabled: x2isDisabled },
    { label: '3x', value: x3, disabled: x3isDisabled },
    { label: '5x', value: x5, disabled: x5isDisabled },
    { label: 'Pot', value: fullPot, disabled: fullPotDisabled },
    { label: 'All In', value: playersStack, disabled: allInDisabled }
  ].filter((p) => !p.disabled);

  return (
    <div
      ref={trayRootRef}
      // EN-10420: the POV decision group measures its clearance against this
      // edge (hooks/useTrayClearanceLift) — the only reliable way to land the
      // same gap on every device.
      data-bet-tray
      style={
        isPortrait
          ? {
              ...portraitBase,
              // Grid cell: the cell's layout decides the width, and the edge
              // treatment is always the rounded panel (see gridCellPanel).
              // The viewport width rule is single-table only. The on-screen
              // keyboard never overlays a desktop grid, so no keyboard offset
              // there.
              ...(isGridMode ? gridCellPanel : isContained ? containedPanel : fullBleedBar),
              ...(isGridMode && gridCellWidth ? { width: gridCellWidth } : null),
              transform:
                !isGridMode && keyboardOffset
                  ? `translateX(-50%) translateY(-${keyboardOffset}px)`
                  : 'translateX(-50%)'
            }
          : {
              ...landscapeBase,
              // Grid cells render portrait today (MultiTableGrid wraps each in
              // an OrientationOverrideProvider), so this branch is not the live
              // grid path — but it must not be able to size a tray from 100vw
              // inside a ~500px cell if that ever changes.
              ...(isGridMode ? gridCellPanel : isContained ? containedPanel : fullBleedBar),
              ...(isGridMode && gridCellWidth ? { width: gridCellWidth } : null)
            }
      }>
      {/* Orange dashed rope line across the top edge of the tray (design) */}
      <div style={dashedTopLine} />

      {/* Preset buttons */}
      <div style={presetRow}>
        {presets.map((p) => {
          const isActive = Number(amount) === p.value;
          const isAllInPreset = p.label === 'All In';
          return (
            <button
              key={p.label}
              onClick={() => setAmount(p.value)}
              style={{
                ...presetBtn,
                ...(isAllInPreset ? presetBtnAllIn : {}),
                ...(isActive ? presetBtnActive : {})
              }}>
              {p.label}
            </button>
          );
        })}
      </div>

      {/* Slider — saloon rail with orange ticks and the white "H" chip handle */}
      <Slider
        value={+amount}
        min={minAmount}
        max={maxAmount}
        step={step}
        marks={sliderMarks}
        onChange={(e) => handleRaiseSlider(e as any)}
        onChangeCommitted={handleRaiseSliderCommit}
        slots={{ thumb: SliderThumb }}
        sx={sliderSx}
      />

      {/* Amount input flanked by minus / plus steppers — plus the in-tray
          countdown to the left of the minus while the clock runs (EN-10420),
          so the player still sees their time while sizing a raise. */}
      <div
        style={{
          ...amtRow,
          ...(showTrayCountdown
            ? {
                gridTemplateColumns: `${Math.round(
                  SEAT_TIMER_SIZE * trayTimerScale
                )}px 36px 1fr 36px`
              }
            : {})
        }}>
        {showTrayCountdown && <TrayCountdownRing show={showTrayCountdown} scale={trayTimerScale} />}
        <button style={amtStep} onClick={() => adjustAmount(-1)} aria-label='Decrease bet'>
          −
        </button>
        <NumericFormat
          customInput={OutlinedInput}
          value={
            hasPotLimit
              ? amount > fullPot
                ? fullPot
                : amount
              : amount > playersStack
              ? playersStack
              : amount
          }
          onChange={updateAmount}
          onPaste={handlePasteValue}
          onFocus={handleAmountFocus}
          onBlur={handleAmountBlur}
          allowNegative={false}
          decimalScale={isPennyTable ? 2 : 0}
          onKeyDown={handleKeyDown}
          size='small'
          fullWidth
          inputProps={{
            // Keep the text input (NumericFormat owns formatting) but open the
            // number pad immediately — players had to tap "123" to switch
            // keyboard modes before they could type a size. Same pairing the
            // Cashier amount fields use (DepositView / WithdrawView).
            inputMode: isPennyTable ? 'decimal' : 'numeric',
            'aria-label': 'Bet amount'
          }}
          // The <input> itself, for the native beforeinput listener above.
          inputRef={attachAmountInput}
          startAdornment={
            isCashTable ? (
              <InputAdornment position='start'>
                <span style={currencyAdornment}>$</span>
              </InputAdornment>
            ) : undefined
          }
          endAdornment={
            <InputAdornment position='end'>
              <span style={metaAdornment}>Min {formatChips(minAmount)}</span>
            </InputAdornment>
          }
          sx={inputSx}
        />
        <button style={amtStep} onClick={() => adjustAmount(1)} aria-label='Increase bet'>
          +
        </button>
      </div>
    </div>
  );
};

export default RaiseActionExpanded;

/* ── Styles ── */

// Saloon bet-tray gradient: warm rust wash over a navy base (design .tp-bettray).
const trayGradient =
  'linear-gradient(180deg, rgba(74,35,10,0.55) 0%, rgba(20,30,48,0.0) 100%), linear-gradient(180deg, #1a2438 0%, #0f1a2d 100%)';

const landscapeBase: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  position: 'absolute',
  // .poker-actions sits inside TopAction's centerSection, which is absolutely
  // centered on screen — so left:50% + translateX(-50%) centers the tray on the
  // viewport. Width comes from the shared variants below (full-bleed bar vs
  // contained panel), decided by viewport width alone.
  left: '50%',
  bottom: '100%',
  transform: 'translateX(-50%)',
  padding: '12px',
  background: trayGradient,
  boxShadow: '0 -8px 24px rgba(0, 0, 0, 0.55)',
  gap: '9px',
  zIndex: 20
};

const trayEdgeLine = '1px solid rgba(232,130,31,0.4)';

// Below the flip point (TRAY_FULL_BLEED_BELOW_PX): full-bleed bar to the
// screen edges — square, top rope only, both orientations. On phones this
// intentionally paints over the floating util stack (trophy / add-on) — see
// the width-rule note in gameConstants. Small bottom gap so the dark footer
// background shows between the tray and the action buttons instead of the
// tray sitting flush on them.
const fullBleedBar: CSSProperties = {
  width: '100vw',
  borderRadius: 0,
  borderTop: trayEdgeLine,
  marginBottom: '0.5rem'
};

// At/above the flip point: a contained, centered panel with a full orange
// outline and rounded corners, floated a touch above the action buttons. The
// fixed side gutters (TRAY_EDGE_GUTTER_PX each) are what keep the floating
// util stack (trophy / add-on) visible and tappable while the tray is open
// (Jason + Marlon huddle 8/13); the cap keeps it a panel rather than a
// ribbon on very wide screens.
const containedPanel: CSSProperties = {
  width: `min(calc(100vw - ${TRAY_EDGE_GUTTER_PX * 2}px), ${TRAY_MAX_WIDTH_PX}px)`,
  marginBottom: '0.5rem',
  border: trayEdgeLine,
  borderRadius: '16px'
};

// Orange dashed "rope" line along the top edge of the tray (design
// .tp-bettray::before). Rendered as a real element since inline styles
// can't express a pseudo-element.
const dashedTopLine: CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  top: -1,
  height: '4px',
  background: 'repeating-linear-gradient(90deg, #e8821f 0 6px, transparent 6px 12px)',
  opacity: 0.55,
  maskImage: 'linear-gradient(90deg, transparent, black 6%, black 94%, transparent)',
  WebkitMaskImage: 'linear-gradient(90deg, transparent, black 6%, black 94%, transparent)',
  pointerEvents: 'none'
};

// Portrait: same saloon gradient and dashed top rope as landscape. Width and
// edge treatment come from the shared variants (fullBleedBar / containedPanel
// — viewport width alone decides, per the width rule in gameConstants);
// left:50% lets either variant break out of the footer's side padding and
// center on the viewport. Transform is applied at the call site so it can
// compose with the keyboard offset.
const portraitBase: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  position: 'absolute',
  left: '50%',
  bottom: '100%',
  padding: '0.75rem',
  background: trayGradient,
  boxShadow: '0 -8px 24px rgba(0, 0, 0, 0.55)',
  gap: '9px',
  zIndex: 50
};

/**
 * The tray inside a GRID CELL: the contained panel, sized to the cell.
 *
 * Two rules, and they are independent — which is what the previous version got
 * wrong by fusing them:
 *
 * 1. WIDTH comes from the cell, never the viewport. `width: 100%` resolves
 *    against the positioned ancestor (`.poker-actions`), so the tray overlays
 *    the action slot exactly and stays inside the cell, where `overflow:
 *    hidden` would otherwise clip a `100vw` tray.
 *
 * 2. EDGE TREATMENT is rounded, always. Full-bleed square edges exist to meet
 *    the SCREEN edge on a phone; in a grid there is another felt immediately
 *    beside this one, so a hard edge reads as a seam between tables rather
 *    than as a panel belonging to one (Jason 2026-08-19). Rounded corners and
 *    a full outline are what separate it from its neighbour.
 *
 * The old override took the width rule from the cell but the EDGE rule from
 * full-bleed — square corners, top rope only — so a grid cell showed the
 * phone's edge-to-edge treatment on a tray that stopped well short of the
 * cell edges. It read as a full-bleed bar that failed to reach the edge.
 *
 * Spreading `containedPanel` rather than restating its border/radius keeps the
 * two in sync: a grid tray IS the contained panel, differing only in what
 * decides its width.
 */
export const gridCellPanel: CSSProperties = {
  ...containedPanel,
  width: '100%'
};

const presetRow: CSSProperties = {
  display: 'flex',
  gap: '6px',
  flexWrap: 'wrap'
};

const presetBtn: CSSProperties = {
  flex: '1 1 3rem',
  minWidth: '3rem',
  height: '32px',
  borderRadius: '6px',
  background: 'rgba(0, 0, 0, 0.45)',
  border: '1px solid rgba(232, 130, 31, 0.35)',
  color: saloonTokens.bone,
  fontFamily: saloonFonts.numeric,
  fontWeight: 700,
  fontSize: '11px',
  letterSpacing: '0.10em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  boxShadow: 'inset 0 1px 0 rgba(255, 180, 92, 0.08)',
  transition: 'all 0.15s ease'
};

const presetBtnActive: CSSProperties = {
  background: `linear-gradient(180deg, ${saloonTokens.orange300}, ${saloonTokens.orange500} 60%, #b85f12)`,
  color: saloonTokens.ink,
  border: '1px solid rgba(0, 0, 0, 0.5)',
  textShadow: '0 1px 0 rgba(255, 220, 160, 0.4)',
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.35), 0 2px 6px rgba(0, 0, 0, 0.4)'
};

const presetBtnAllIn: CSSProperties = {
  background: 'linear-gradient(180deg, rgba(196, 39, 26, 0.18), rgba(107, 26, 20, 0.18))',
  border: '1px solid rgba(196, 39, 26, 0.55)',
  color: '#ff9a8c'
};

const amtRow: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '36px 1fr 36px',
  gap: '6px',
  alignItems: 'center'
};

const currencyAdornment: CSSProperties = {
  fontFamily: saloonFonts.decorative,
  fontSize: '14px',
  color: saloonTokens.orange300
};

const metaAdornment: CSSProperties = {
  fontFamily: saloonFonts.typewriter,
  fontSize: '9px',
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: saloonTokens.bone2,
  opacity: 0.75,
  whiteSpace: 'nowrap'
};

const amtStep: CSSProperties = {
  height: '38px',
  borderRadius: '6px',
  background: 'rgba(0, 0, 0, 0.45)',
  border: '1px solid rgba(232, 130, 31, 0.4)',
  color: saloonTokens.orange300,
  fontFamily: saloonFonts.numeric,
  fontWeight: 700,
  fontSize: '18px',
  lineHeight: 1,
  cursor: 'pointer',
  boxShadow: 'inset 0 1px 0 rgba(255, 180, 92, 0.1)'
};

// White marble chip handle stamped with "H" (design .tp-slider-handle). Wrap
// MUI's styled SliderThumb (NOT a bare span) so the handle keeps its base
// position:absolute + value-driven `left`, then append the "H" label.
const SliderThumb = (props: any) => {
  const { children, ...other } = props;
  return (
    <MuiSliderThumb {...other}>
      {children}
      <span style={thumbLabel}>H</span>
    </MuiSliderThumb>
  );
};

const thumbLabel: CSSProperties = {
  fontFamily: saloonFonts.decorative,
  fontSize: '10px',
  color: '#1a1208',
  pointerEvents: 'none'
};

const sliderSx = {
  height: 8,
  padding: '13px 0',
  '& .MuiSlider-rail': {
    height: 8,
    borderRadius: 999,
    opacity: 1,
    background: 'linear-gradient(180deg, #2b170a, #1a0e06)',
    border: '1px solid #1a0e06',
    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.6)'
  },
  '& .MuiSlider-track': {
    height: 8,
    border: 'none',
    borderRadius: 999,
    background: 'linear-gradient(180deg, #ffd089, #f59838 50%, #c46410)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.5), 0 0 6px rgba(255,180,92,0.6)'
  },
  '& .MuiSlider-mark': {
    width: '1px',
    height: '14px',
    borderRadius: 0,
    opacity: 1,
    backgroundColor: 'rgba(232,130,31,0.4)'
  },
  '& .MuiSlider-markActive': {
    backgroundColor: 'rgba(232,130,31,0.4)',
    opacity: 1
  },
  '& .MuiSlider-thumb': {
    width: 22,
    height: 22,
    background: 'radial-gradient(circle at 35% 30%, #ffffff, #d6d2c6 70%, #8b8779 100%)',
    border: '1.5px solid #1a0e06',
    boxShadow: '0 3px 8px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.6)',
    '&::before': { boxShadow: 'none' },
    '&:hover, &.Mui-focusVisible, &.Mui-active': {
      boxShadow: '0 3px 8px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.6)'
    }
  }
};

const inputSx = {
  height: '38px',
  backgroundColor: 'rgba(0,0,0,0.55)',
  borderRadius: '6px',
  color: saloonTokens.bone,
  fontFamily: saloonFonts.numeric,
  fontWeight: 700,
  fontSize: '18px',
  letterSpacing: '0.02em',
  boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.5)',
  '& input': {
    textAlign: 'center',
    padding: '0 12px'
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(232,130,31,0.4)'
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(232,130,31,0.6)'
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: saloonTokens.orange500
  }
};
